import React, { useRef, useState } from 'react';
import {
    AlertTriangle, ArrowDown, ArrowUp, FileText, Info, Loader2, RotateCw, Trash2, Upload, X,
} from 'lucide-react';
import { leseEinleseSeiten } from '../../services/pdf/vorankScanNormalisieren';
import { vorauswahlSeiten } from '../../services/pdf/vorankScanMasse';
import type { VorankFassungZeile } from '../../services/vorank/vorankUnterschriftTypes';
import { describeError } from '../../services/errorMessage';
import { liesUnterschriebeneDateiEin } from './vorankUnterschriftAblauf';
import {
    VORANK_EINLESEN_ACCEPT,
    vorankDreheWeiter,
    vorankEinleseAuftrag,
    vorankEinleseKandidaten,
    vorankEinleseSperre,
    vorankEinleseVorgabe,
    vorankFassungOption,
    vorankNeueEintraege,
    vorankPruefeEinleseDateien,
    vorankSeitenAnzahl,
    vorankSeitenHinweis,
    vorankSeitenKey,
    vorankTeilAbgelegtText,
    vorankVerschiebe,
    vorankWahlMitVorauswahl,
    type VorankEinleseEintrag,
    type VorankSeitenWahlen,
} from './vorankUnterschriftAnsicht';

interface Props {
    projektId: string;
    /** Alle Fassungen des Projekts — zur Wahl stehen die noch nicht unterschriebenen. */
    fassungen: readonly VorankFassungZeile[];
    onSchliessen: () => void;
    /** `bereits`: die Fassung war schon als unterschrieben vermerkt (anderes Fenster). */
    onEingelesen: (bereits: boolean) => void;
}

interface Stand {
    eintraege: VorankEinleseEintrag<File>[];
    wahl: VorankSeitenWahlen;
}

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg outline-none '
    + 'focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';
const KLEIN = 'p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed';

const groesseText = (bytes: number): string =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Papierweg (Plan §8): eine per Mail zurückgekommene, unterschriebene
 * Vorankündigung einlesen — PDF (auch Adobe-signiert), Scan oder Handyfotos.
 * Dateien wählen und ordnen, der Fassung zuordnen, Seiten mitnehmen und
 * drehen; „Einlesen“ bringt sie auf A4, legt Original und A4-PDF in
 * „Dokumente“ ab und vermerkt die Unterschrift an der Fassung.
 *
 * Abläufe in `vorankUnterschriftAblauf.ts`, Regeln in `vorankUnterschriftAnsicht.ts`.
 */
export const VorankEinlesenDialog: React.FC<Props> = ({ projektId, fassungen, onSchliessen, onEingelesen }) => {
    const [jetzt] = useState(() => new Date());
    const kandidaten = vorankEinleseKandidaten(fassungen);
    const [fassungId, setFassungId] = useState<string | null>(() => vorankEinleseVorgabe(vorankEinleseKandidaten(fassungen)));
    const fassung = kandidaten.find(f => f.id === fassungId) ?? null;

    const [stand, setStand] = useState<Stand>({ eintraege: [], wahl: {} });
    const [name, setName] = useState('');
    const [liest, setLiest] = useState(false);
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    /** Titel dessen, was ein abgebrochener Lauf schon in „Dokumente“ gelegt hat — es bleibt dort. */
    const [teilAbgelegt, setTeilAbgelegt] = useState<string[]>([]);
    const dateiRef = useRef<HTMLInputElement>(null);
    const nummerRef = useRef(0);
    const laeuftRef = useRef(false);

    const seitenGesamt = vorankSeitenAnzahl(stand.eintraege);
    const auftrag = vorankEinleseAuftrag(stand.eintraege, stand.wahl);
    const mitgenommen = auftrag.auswahl.seiten.length;
    const sperre = vorankEinleseSperre({ fassung, seitenGesamt, mitgenommen, liest });
    const seitenHinweis = fassung ? vorankSeitenHinweis(mitgenommen, fassung.seiten) : null;
    const teilText = vorankTeilAbgelegtText(teilAbgelegt);
    const gesperrt = laeuft || liest;

    const dateienGewaehlt = (liste: FileList | null) => {
        const neu = Array.from(liste || []);
        if (dateiRef.current) dateiRef.current.value = '';
        if (!neu.length || gesperrt) return;
        const gruende = vorankPruefeEinleseDateien(neu);
        if (gruende.length) {
            setFehler(gruende.join(' '));
            return;
        }
        setFehler(null);
        setLiest(true);
        const nummer = nummerRef.current;
        nummerRef.current += neu.length;
        const fassungSeiten = fassung?.seiten ?? 0;
        void leseEinleseSeiten(neu).then(
            seiten => {
                setStand(prev => {
                    const alle = [...prev.eintraege, ...vorankNeueEintraege(neu, seiten, nummer)];
                    const vorauswahl = vorauswahlSeiten(vorankSeitenAnzahl(alle), fassungSeiten);
                    return { eintraege: alle, wahl: vorankWahlMitVorauswahl(alle, vorauswahl, prev.wahl, false) };
                });
                setLiest(false);
            },
            (e: unknown) => {
                setLiest(false);
                // Der Leser nennt Datei und Ausweg selbst (HEIC, Kennwort, Größe …).
                setFehler(describeError(e));
            },
        );
    };

    const fassungWaehlen = (id: string) => {
        setFassungId(id);
        const seiten = kandidaten.find(k => k.id === id)?.seiten ?? 0;
        // Andere Fassung, andere Seitenzahl: die Vorauswahl gilt neu, die Drehungen bleiben.
        setStand(prev => ({
            ...prev,
            wahl: vorankWahlMitVorauswahl(prev.eintraege, vorauswahlSeiten(vorankSeitenAnzahl(prev.eintraege), seiten), prev.wahl, true),
        }));
    };

    const mitnehmenUmschalten = (key: string) => setStand(prev => {
        const alt = prev.wahl[key];
        if (!alt) return prev;
        return { ...prev, wahl: { ...prev.wahl, [key]: { ...alt, mitnehmen: !alt.mitnehmen } } };
    });

    const drehen = (key: string) => setStand(prev => {
        const alt = prev.wahl[key];
        if (!alt) return prev;
        return { ...prev, wahl: { ...prev.wahl, [key]: { ...alt, drehung: vorankDreheWeiter(alt.drehung) } } };
    });

    const verschieben = (index: number, richtung: -1 | 1) =>
        setStand(prev => ({ ...prev, eintraege: vorankVerschiebe(prev.eintraege, index, richtung) }));

    const entfernen = (id: string) =>
        setStand(prev => ({ ...prev, eintraege: prev.eintraege.filter(e => e.id !== id) }));

    const einlesen = () => {
        if (laeuftRef.current || sperre || !fassung) return;
        laeuftRef.current = true;
        setLaeuft(true);
        setFehler(null);
        void liesUnterschriebeneDateiEin({
            projektId,
            fassung,
            dateien: auftrag.dateien,
            auswahl: auftrag.auswahl,
            name,
            jetzt: new Date().toISOString(),
        }).then(ergebnis => {
            laeuftRef.current = false;
            setLaeuft(false);
            if (ergebnis.art === 'ok') {
                onEingelesen(ergebnis.bereits);
                return;
            }
            setFehler(ergebnis.fehler);
            setTeilAbgelegt(prev => [...prev, ...ergebnis.abgelegt.filter(t => !prev.includes(t))]);
        });
    };

    return (
        <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 animate-fade-in">
            <div role="dialog" aria-modal="true" aria-label="Unterschriebene Datei einlesen"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-slate-800">Unterschriebene Datei einlesen</h3>
                        <p className="text-xs text-slate-500">PDF, Scan oder Fotos — wird auf A4 gebracht und als unterschriebene Fassung abgelegt.</p>
                    </div>
                    <button type="button" onClick={onSchliessen} disabled={laeuft} title="Schließen"
                        className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40">
                        <X className="w-4 h-4 text-slate-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {kandidaten.length === 0 ? (
                        <p className="text-sm text-slate-600">
                            Es gibt keine Fassung, die noch auf eine Unterschrift wartet. Erst mit Begleitblatt exportieren — das legt die Fassung an.
                            {' '}Ein Stand, der schon unterschrieben ist, braucht keine zweite.
                        </p>
                    ) : (
                        <label className="block">
                            <span className={LABEL}>Gehört zur Fassung</span>
                            <select value={fassungId ?? ''} onChange={e => fassungWaehlen(e.target.value)} disabled={gesperrt} className={FELD}>
                                {kandidaten.map(f => <option key={f.id} value={f.id}>{vorankFassungOption(f, jetzt)}</option>)}
                            </select>
                        </label>
                    )}

                    <div className="space-y-2">
                        <p className={LABEL}>Dateien</p>
                        <button
                            type="button"
                            onClick={() => dateiRef.current?.click()}
                            disabled={gesperrt || kandidaten.length === 0}
                            className="w-full min-h-[64px] px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 bg-slate-50 border border-dashed border-slate-300 hover:border-blue-400 rounded-xl disabled:opacity-40"
                        >
                            {liest ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                            {liest ? 'Seiten werden gelesen …' : stand.eintraege.length ? 'Weitere Datei hinzufügen' : 'Datei wählen (PDF, JPG, PNG — mehrere Fotos möglich)'}
                        </button>
                        <input
                            ref={dateiRef}
                            type="file"
                            multiple
                            accept={VORANK_EINLESEN_ACCEPT}
                            className="hidden"
                            onChange={e => dateienGewaehlt(e.target.files)}
                        />

                        {stand.eintraege.map((eintrag, index) => (
                            <div key={eintrag.id} className="border border-slate-200 rounded-xl p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                                    <p className="text-xs font-bold text-slate-700 truncate flex-1 min-w-0" title={eintrag.datei.name}>
                                        {index + 1}. {eintrag.datei.name}
                                        <span className="font-normal text-slate-400"> · {groesseText(eintrag.datei.size)}</span>
                                    </p>
                                    <button type="button" onClick={() => verschieben(index, -1)} disabled={laeuft || index === 0}
                                        className={KLEIN} title="Nach vorn">
                                        <ArrowUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => verschieben(index, 1)} disabled={laeuft || index === stand.eintraege.length - 1}
                                        className={KLEIN} title="Nach hinten">
                                        <ArrowDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => entfernen(eintrag.id)} disabled={laeuft}
                                        className={KLEIN} title="Datei entfernen">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                {eintrag.seiten.length === 0 ? (
                                    <p className="text-[11px] text-slate-500">Keine Seite gefunden.</p>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {eintrag.seiten.map(seite => {
                                            const key = vorankSeitenKey(eintrag.id, seite.seitenIndex);
                                            const w = stand.wahl[key];
                                            const mit = !!w?.mitnehmen;
                                            return (
                                                <div key={key} className={`rounded-lg border p-1.5 space-y-1 ${mit ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 opacity-70'}`}>
                                                    <div className="aspect-square bg-white rounded flex items-center justify-center overflow-hidden">
                                                        {seite.vorschau
                                                            ? <img src={seite.vorschau} alt={`Seite ${seite.seitenIndex + 1}`} className="max-w-full max-h-full object-contain transition-transform"
                                                                style={{ transform: `rotate(${w?.drehung ?? 0}deg)` }} />
                                                            : <FileText className="w-6 h-6 text-slate-300" />}
                                                    </div>
                                                    <div className="flex items-center justify-between gap-1">
                                                        <label className="flex items-center gap-1 text-[11px] text-slate-700">
                                                            <input type="checkbox" checked={mit} onChange={() => mitnehmenUmschalten(key)} disabled={laeuft} />
                                                            {eintrag.seiten.length > 1 ? `S. ${seite.seitenIndex + 1}` : 'mitnehmen'}
                                                        </label>
                                                        <button type="button" onClick={() => drehen(key)} disabled={laeuft} className={KLEIN} title="drehen 90°">
                                                            <RotateCw className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                    {seite.hatAnnotationen && (
                                                        <p className="text-[10px] leading-tight text-slate-500">Mit Adobe-Unterschrift/Formularfeld — wird als Bild übernommen.</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                        {seitenHinweis && (
                            <p className="text-xs text-amber-700 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-px" /> {seitenHinweis}</p>
                        )}
                    </div>

                    <label className="block max-w-sm">
                        <span className={LABEL}>Unterschrieben von (optional)</span>
                        <input value={name} onChange={e => setName(e.target.value)} disabled={laeuft} placeholder="Name des Unterzeichners" className={FELD} />
                    </label>

                    {fehler && (
                        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span className="flex-1 break-words">{fehler}</span>
                        </div>
                    )}
                    {teilText && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
                            <Info className="w-4 h-4 shrink-0 mt-px" /> <span className="flex-1 break-words">{teilText}</span>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-200 flex flex-wrap justify-end gap-2 shrink-0">
                    <button type="button" onClick={onSchliessen} disabled={laeuft}
                        className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">
                        {teilAbgelegt.length ? 'Schließen' : 'Abbrechen'}
                    </button>
                    <button type="button" onClick={einlesen} disabled={laeuft || !!sperre} title={sperre ?? undefined}
                        className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-40">
                        {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Einlesen
                    </button>
                </div>
            </div>
        </div>
    );
};
